// Pure formatting helpers for answers and solutions, shared by the on-screen
// exercise runner and the server-rendered PDF print view.
//
// They live here rather than in ExerciseItem.tsx because that file is a
// "use client" module: exporting a plain function from it makes the function a
// client reference, and a server component calling it fails at runtime with
// "Attempted to call X() from the server but X is on the client". Nothing in
// here touches the DOM or React, so a neutral module is where it belongs.

/**
 * Svarsvärdet scan-rättningen skriver på ett fotat svar (api/scan/upload).
 * Lösningen ligger i bilden, inte i `Forsok.svar` — strängen är alltså en
 * markör för "det här försöket är en inskannad lösning", inte ett elevsvar.
 *
 * Delad definition eftersom tre ställen måste hålla exakt samma sträng: routen
 * som skriver den, lärarens diagnosanalys som räknar bort den ur felsvaren, och
 * ExerciseItem som varken får förifylla svarsrutan med den eller tappa bort
 * elevens bild när uppgiften slutar kräva lösning.
 */
export const SKANNAT_SVAR = "[Skannad lösning]";

/**
 * Render a short answer (facit/svar) as KaTeX when it's a bare expression.
 * Authors write svar like "x^2+15x" without $-delimiters, so an inline-math
 * renderer would print the caret literally. If the answer has no $ already and
 * contains no multi-letter word (unit/prose like "kr", "eller", "sant"), wrap
 * the whole thing in $…$ so it renders as math. Decimal commas are tightened to
 * {,} so they don't get math-comma spacing ("1,5", not "1, 5"). Plain numbers
 * ("4") render identically; answers with words/units are left as text.
 */
export function mathifyAnswer(s: string): string {
  // A spreadsheet formula is shown exactly as the student types it — the way
  // the question prints it. Its `$` locks a row or column (B$1, $B$6), but
  // the check below read "=B$1/B$3" as authored math and InlineMath paired
  // the two dollars: "=B" + italic "1/B" + "3" (BUG-2026-121). The answer
  // itself cannot carry `\$`: grading compares it with the student's input.
  // Formulas without `$` were mathified too, so "=A1*B1" printed as A1·B1.
  if (arKalkylformel(s)) return s.trim().replace(/\$/g, "\\$");

  if (s.includes("$")) return s; // already authored with math delimiters

  // "\%" is LaTeX's escaped percent; outside $…$ nothing decodes it and the
  // backslash printed verbatim ("SVAR: 15,9\%", found 2026-08-15). The %-guard
  // below keeps the result as text either way.
  s = s.replace(/\\%/g, "%");

  // Input-syntax roots ("sqrt(3)/2") hit the word guard below ("sqrt" reads as
  // prose) and printed verbatim in SVAR (Enhetscirkeln Ö02/Ö07b, 2026-08-15).
  // Decode to LaTeX so the backslash rule mathifies the answer.
  s = s.replace(/\bsqrt\(([^()]+)\)/g, "\\sqrt{$1}");

  // A backslash command (\dfrac, \sqrt, \cdot, \lg …) is unambiguous LaTeX —
  // no unit or Swedish word starts with a backslash. Without this the word
  // guard below reads "dfrac" as prose and the answer prints as raw source:
  // "SVAR: b=\dfrac{40}{h}" in a printed facit (found 2026-08-15, NP-blandad
  // uppgift in losa-ut-ur-formler; same class in exponentialfunktioner,
  // potensfunktioner, absolutbelopp, rakneregler-for-logaritmer …). The
  // KaTeX-special guard still wins: % starts a comment and would swallow the
  // rest of the answer.
  // "z = …\sqrt{3}/2i eller z = …": the backslash rule would drag the prose
  // conjunction into math mode and print "2iellerz" glued (Binomiska
  // ekvationer Ö03, 2026-08-15) — mathify each side separately instead.
  // NP answers spell the conjunction inside math: "v\le 74 \text{ eller }
  // v\ge 86". The split below then cut through the \text{…} braces and each
  // half became a KaTeX error (Egenskaper hos normalfördelat material, PDF-
  // körningen 2026-09-14). Lift the conjunction out first; the halves are
  // then mathified separately like any other "… eller …" answer.
  s = s.replace(/\\text\{\s*(eller|och)\s*\}/g, " $1 ");

  if (/\s(eller|och)\s/.test(s) && /\\[a-zA-Z]{2,}/.test(s)) {
    return s.split(/\s+(eller|och)\s+/).map((del, i) =>
      i % 2 === 1 ? del : mathifyAnswer(del)).join(" ");
  }

  if (/\\[a-zA-Z]{2,}/.test(s) && !/[%#&]/.test(s)) {
    // Same input-syntax clean-up as the compact branch: "e^x/(2*\sqrt{x})"
    // reached here via the sqrt rule and printed ∗ (Produktregeln 2026-09-14).
    return `$${stadaInmatningssyntax(s)}$`;
  }

  // A number, a space, and a short run of letters is a measurement — the space
  // is what separates "15 m" from a product. The word-guard below needs TWO
  // consecutive letters, so single-letter units fell straight through it: "15 m"
  // was mathified, KaTeX set the unit as an italic variable and swallowed the
  // space, and the answer printed as "15m" — read as 15·m. Meanwhile "5 km" and
  // "10 kr" survived by sheer luck of having two letters. Measured 2026-08-14:
  // 331 answers, every trailing token a real SI unit (m, s, g, J, L, h, l) and
  // not one a variable product. Units stay text, like every other unit answer.
  // The optional trailing ²/³ exists because "2000000 m²" fell through: "m"
  // alone is no two-letter word, the superscript broke the $-anchor, and the
  // answer was mathified into an italic glued "2000000m²" (found 2026-08-15 in
  // a printed facit). "cm²" survived only because "cm" tripped the word guard.
  // …except the imaginary unit: "sqrt(3)/2 i" is a complex number, not three
  // halves of an SI unit i (Binomiska ekvationer, 2026-09-14). A trailing i is
  // imaginary when the answer also carries a root, a backslash command or a
  // "z =" — no measurement has any of those.
  const imaginarEnhet = /\d\s+i$/.test(s.trim()) && /sqrt|\\|\bz\s*=/.test(s);
  if (!imaginarEnhet && /\d\s+[A-Za-zÅÄÖåäö]{1,3}[²³]?(?:\/[A-Za-zÅÄÖåäö]{1,3})?$/.test(s.trim())) return s;

  // A compact expression — no spaces, no real (3+ letter) word, at least one
  // operator — is maths even when it contains two-letter products like "ab"
  // or "ax": the word guard read those as prose and printed raw carets
  // ("a^2-2ab+b^2", "(2ax+3b)(2ax-3b)", found 2026-08-15). Two-letter UNITS
  // never appear space-free next to operators. Mixed prose+math answers
  // ("… då x-y=1") stay text — they must be authored with $…$.
  const kompaktUttryck =
    !/\s/.test(s) && !/[A-Za-zÅÄÖåäö]{3,}/.test(s) && /[\^()+=/-]/.test(s);
  if (!kompaktUttryck && /[A-Za-zÅÄÖåäö]{2,}/.test(s)) return s; // word/unit → text
  if (/[%#&]/.test(s)) return s; // KaTeX-special chars (% is a comment) → keep as text
  return `$${stadaInmatningssyntax(s)}$`;
}

// A spreadsheet formula: a leading "=", then only what a formula is made of —
// letters, digits, $, operators, ranges, argument separators and decimal
// commas — and at least one cell reference (column letters, then a row
// number, either part optionally locked with $). No maths answer starts with
// "=" (2026-10-25: all 76 such answers in the bank are spreadsheet formulas),
// and the cell-reference requirement keeps "=3" or "=x+1" out. Lower case is
// allowed because spreadsheets accept it, so a student's own "=b$1/b$3"
// echoed back on the results page is shown literally too.
const KALKYLFORMEL_RE = /^=[A-Za-zÅÄÖåäö0-9$():;,.+\-*/^ ]+$/;
const CELLREFERENS_RE = /(?:^|[^A-Za-zÅÄÖåäö])\$?[A-Za-z]{1,3}\$?\d/;

export function arKalkylformel(s: string): boolean {
  const t = s.trim();
  return KALKYLFORMEL_RE.test(t) && CELLREFERENS_RE.test(t);
}

/** Input-syntax → LaTeX for display only. Shared by both mathify branches. */
function stadaInmatningssyntax(s: string): string {
  return s
    .replace(/(\d),(\d)/g, "$1{,}$2")
    // Authors write the answer in the student's input syntax ("d * t",
    // "3x^2*h"). KaTeX sets a bare asterisk as ∗ (U+2217), which is neither the
    // \cdot kanon §32 requires nor what the solution two lines below uses — the
    // same product appeared with two different multiplication signs on one page.
    // 133 answers, all genuine multiplications.
    .replace(/\s*\*\s*/g, " \\cdot ")
    // Same input-syntax family: "1,8^(n-1)". KaTeX superscripts exactly one
    // token, so only "(" went up and "n-1)" fell back to the baseline (found
    // 2026-08-15 in a printed facit). Nested parentheses are left alone.
    // One level of nesting inside the exponent is common in the chain rule
    // ("e^((t^2+1)^2)", Kedjeregeln 2026-09-14); deeper nesting is left alone.
    .replace(/\^\(([^()]*(?:\([^()]*\)[^()]*)*)\)/g, "^{$1}")
    // Bare multi-digit exponents have the same failure: KaTeX superscripts a
    // single token, so "a^8/b^12" printed as "b¹2" (found 2026-08-15).
    .replace(/\^(\d{2,})/g, "^{$1}")
    // Negative exponents too: "10^-2" superscripted only the minus sign.
    .replace(/\^(-\d+)/g, "^{$1}")
    // "lg7/lg5" passed the compact-expression rule but KaTeX set lg as the
    // variables l·g (found 2026-08-15) — upright the log operators.
    .replace(/(?<!\\)\b(lg|ln)\b/g, "\\$1 ");
}

/**
 * One run of the source, either prose or a formula, with the source indices it
 * came from. `value` is what the renderer should typeset — for text runs an
 * escaped `\$` has already been unescaped to `$` — while `start`/`end` point at
 * the untouched original, which is what a rewriter must slice so the escape
 * survives a round trip.
 */
export interface Segment {
  kind: "text" | "math";
  value: string;
  block: boolean;
  start: number;
  end: number;
}

/**
 * Split a source string into prose and math runs.
 *
 * This is the single place that knows where a formula begins and ends. It lives
 * in this neutral module rather than in InlineMath because that file is
 * "use client": anything that must also run during server rendering — the print
 * view, splitIntoAiryParagraphs below — cannot import from there. Two copies of
 * the delimiter rules would drift, and a rewriter that guesses wrong edits the
 * inside of a formula.
 */
/**
 * Finds the closing delimiter of a formula, skipping `\$` — a literal dollar
 * inside the maths. A plain indexOf ended `$\texttt{=B\$1/B\$3}$` at the
 * first `\$`, so every spreadsheet cell reference with an absolute row
 * rendered as a KaTeX error followed by raw source (Lån, ränta och
 * amortering, 2026-09-14). An even run of backslashes (`\\$`) is a line
 * break followed by a real delimiter and must still close.
 */
function hittaSlutdelimiter(source: string, delim: string, from: number): number {
  let i = source.indexOf(delim, from);
  while (i !== -1) {
    let backslashes = 0;
    for (let j = i - 1; j >= from && source[j] === "\\"; j--) backslashes++;
    if (backslashes % 2 === 0) return i;
    i = source.indexOf(delim, i + 1);
  }
  return -1;
}

/**
 * End index of an inline code span `…` opening at `i` — a single backtick pair on
 * one line, never part of a ``` fence — or -1. Content authors write program
 * code, spreadsheet formulas and tool syntax this way (≈ 9 000 spans in the
 * banks); a `$` inside one (`$B2`, `=B\$1`) is a spreadsheet dollar, not a
 * formula delimiter.
 */
function kodSpannSlut(source: string, i: number): number {
  if (source[i + 1] === "`" || source[i - 1] === "`") return -1;
  const slut = source.indexOf("`", i + 1);
  if (slut === -1 || slut === i + 1 || source[slut + 1] === "`") return -1;
  const radslut = source.indexOf("\n", i + 1);
  return radslut !== -1 && radslut < slut ? -1 : slut;
}

/**
 * Splits a prose run into plain text and inline code spans (backticks removed).
 * Single backticks used to print verbatim ("`tal[0]` är 7"), and `*` inside a
 * span was read as emphasis (BUG-2026-139).
 */
export function delaInlineKod(text: string): { kod: boolean; value: string }[] {
  const delar: { kod: boolean; value: string }[] = [];
  let fran = 0;
  for (let i = 0; i < text.length; i++) {
    if (text[i] !== "`") continue;
    const slut = kodSpannSlut(text, i);
    if (slut === -1) continue;
    if (i > fran) delar.push({ kod: false, value: text.slice(fran, i) });
    delar.push({ kod: true, value: text.slice(i + 1, slut) });
    fran = slut + 1;
    i = slut;
  }
  if (fran < text.length) delar.push({ kod: false, value: text.slice(fran) });
  return delar;
}

export function parseSegments(source: string): Segment[] {
  const segments: Segment[] = [];
  let i = 0;
  let buf = "";
  let bufStart = 0;

  const flushText = () => {
    if (buf.length > 0) {
      segments.push({ kind: "text", value: buf, block: false, start: bufStart, end: i });
      buf = "";
    }
    bufStart = i;
  };

  while (i < source.length) {
    const ch = source[i];

    // A `\\` pair is consumed whole: it is the authored paragraph break, and
    // letting its second backslash half-match the `\$`-escape below ate the
    // `$` that opened the next formula — every later `$` shifted one step and
    // the rest of the solution printed as raw source ("…13$ \\$(9,4)$…",
    // found 2026-08-15 in Avståndsformeln).
    if (ch === "\\" && source[i + 1] === "\\") {
      buf += "\\\\";
      i += 2;
      continue;
    }

    if (ch === "\\" && i + 1 < source.length && source[i + 1] === "$") {
      buf += "$";
      i += 2;
      continue;
    }

    // An inline code span stays prose: its `$` is a spreadsheet dollar, so it
    // must neither open a formula nor close one opened later in the string.
    if (ch === "`") {
      const slut = kodSpannSlut(source, i);
      if (slut !== -1) {
        buf += source.slice(i, slut + 1).replace(/\\\$/g, "$");
        i = slut + 1;
        continue;
      }
    }

    if (ch === "$") {
      const block = source[i + 1] === "$";
      const delimLen = block ? 2 : 1;
      const end = hittaSlutdelimiter(source, block ? "$$" : "$", i + delimLen);
      if (end === -1) {
        buf += ch;
        i++;
        continue;
      }
      flushText();
      const start = i;
      i = end + delimLen;
      segments.push({
        kind: "math",
        value: source.slice(start + delimLen, end),
        block,
        start,
        end: i,
      });
      bufStart = i;
      continue;
    }

    // Bare LaTeX environments — treat \begin{name}…\end{name} (e.g.
    // align*, gather, cases) as display math even without surrounding
    // $$. Lets paket-författare skriva block-matte direkt utan att
    // komma ihåg dollar-omslaget.
    if (ch === "\\" && source.startsWith("\\begin{", i)) {
      const nameEnd = source.indexOf("}", i + 7);
      if (nameEnd !== -1) {
        const name = source.slice(i + 7, nameEnd);
        const closer = `\\end{${name}}`;
        const closerStart = source.indexOf(closer, nameEnd + 1);
        if (closerStart !== -1) {
          const closerEnd = closerStart + closer.length;
          flushText();
          const start = i;
          i = closerEnd;
          segments.push({
            kind: "math",
            value: source.slice(start, closerEnd),
            block: true,
            start,
            end: i,
          });
          bufStart = i;
          continue;
        }
      }
    }

    buf += ch;
    i++;
  }

  flushText();
  return segments;
}

// A lone `\\` in prose is LaTeX's line break, written by paket-authors to
// separate the steps of a solution. Nothing downstream understood it: KaTeX
// never saw it (it sits outside $…$) and the renderer prints text verbatim, so
// every solution shipped a literal "\\" mid-sentence — 5 326 of them across 98
// paket files, on screen as well as on paper. Measured 2026-08-14 in
// Multiplikation och division av tal i bråkform. It is a paragraph break, so
// that is what it becomes. The whitespace on both sides is required: it keeps
// the pattern off anything that merely contains two backslashes.
// 2026-08-15: the whitespace-before requirement missed `\\` glued straight
// onto punctuation ("…ekvationen.\\ Substituera…") — 56 literala "\\" i ETT
// facit (Substitutionsmetoden). A `\\` right after .,:;!?) followed by
// space/end is equally unambiguous, so it breaks too.
// Same day, the other side: `\\` glued onto the NEXT word ("…brantare.
// \\Svar: …" — 21 lösningar in Anpassning till andra modeller printed the
// literal "\Svar"). A `\\` followed by a capitalized word in prose is the same
// authored paragraph break; a real LaTeX command would sit inside $…$ and
// never reach this rewrite. Newlines count as the surrounding whitespace too.
const RADBRYTNING_RE =
  /(?:(?:^|\s)|(?<=[.,:;!?)]))\\\\(?=\s|$|[A-ZÅÄÖ])\s*/g;

// Split a solution/teori string into airier paragraphs. Breaks are inserted
// before common Swedish cue words ("Exempel:", "Svar:", "Formel:", "Alltså",
// …) so a single dense sentence becomes two or three visually separated
// paragraphs. Explicit `\n\n` in the source also creates paragraph breaks.
const CUE_WORDS = [
  "Exempel",
  "Formel",
  "Svar",
  "Alltså",
  "Därför",
  "Slutsats",
  "Kontroll",
  "Beräkning",
  "Notera",
  "Observera",
  "Lösning",
  "Steg",
];
const CUE_RE = new RegExp(
  `([.!?][)'"’”]*)\\s+(${CUE_WORDS.join("|")})(?=[\\s:])`,
  "g",
);

export function splitIntoAiryParagraphs(source: string): string[] {
  // The `\\` rewrite runs per prose run, never across a formula: a `\\` inside
  // $$…$$ or \begin{cases}…\end{cases} is a row separator, and turning it into
  // a blank line would tear the environment in half. Text runs are sliced from
  // the original rather than taken from `value` so an escaped `\$` stays
  // escaped — these strings get parsed again by the renderer.
  const utanRadbrytning = parseSegments(source)
    .map((s) =>
      s.kind === "math"
        ? source.slice(s.start, s.end)
        : source
            .slice(s.start, s.end)
            .replace(RADBRYTNING_RE, "\n\n")
            // "\\\\[6pt]" is LaTeX's spaced line break — authored in prose it
            // printed verbatim, [6pt] included (12 lösningar in Ma3
            // Tillämpningar 1c, 2026-08-15).
            .replace(/\\\\\[\d+pt\]\s*/g, "\n\n")
            // Authors also write "\n" hoping for a newline — the TS string
            // carries a literal backslash-n and it printed verbatim (35 ställen
            // in Ma3 Addition och subtraktion, 2026-08-15). Only before a
            // capital or whitespace: \nabla etc. must survive.
            .replace(/\\n(?=[A-ZÅÄÖ\s•–-])/g, "\n\n"),
    )
    .join("");
  // CUE_RE only matches `.`/`!`/`?` followed by whitespace + cue word — a
  // math segment's closing `$` sits between any math-internal period and
  // the cue word, so the pattern cannot fire inside $...$.
  const withBreaks = utanRadbrytning.replace(CUE_RE, "$1\n\n$2");
  return withBreaks
    .split(/\n\n+/)
    .map((p) => p.trim())
    .filter((p) => p.length > 0);
}

/**
 * Display-only repairs of authored markup in a PROSE run (a text segment from
 * parseSegments — never the maths inside $…$). InlineMath applies this to every
 * text segment before printing it verbatim; the content validator applies the
 * same function to find what would still print as a raw backslash
 * (prisma/backslash-i-prosa.ts), so the two can never disagree about which
 * forms are repaired.
 */
export function reparaProsa(text: string): string {
  return (
    text
      // Solution prose sometimes carries step arrows OUTSIDE the math
      // delimiters ("… \Rightarrow Ur grafen …") — 16 lösningar in ONE
      // delavsnitt printed the literal command (found 2026-08-15).
      // Substitution to the arrow glyph.
      .replace(/\\(Rightarrow|Leftrightarrow)(?![a-zA-Z])/g, (m) =>
        m === "\\Rightarrow" ? "⇒" : "⇔",
      )
      // Same class: spreadsheet formulas authored as \texttt{=A1+B1} in prose
      // printed the raw command (11 places in En introduktion till
      // kalkylprogram, 2026-08-15). The inner text is what the reader needs.
      .replace(/\\texttt\{([^{}]*)\}/g, "$1")
      // Authored HTML entities reach print as literal text ("&nbsp;&nbsp;" in
      // a rangordna item, "Säkerhets&shy;ingenjörer" in 68–95–99,7-regeln,
      // both found 2026-08-15) — decode the common typographic ones.
      .replace(/&nbsp;/g, " ")
      .replace(/&shy;/g, "­")
      // Authored <em>/<strong> tags print verbatim (Kan alla funktioner
      // deriveras?, 2026-08-15) — fold them into the *-/**-emphasis pipeline.
      .replace(/<em>([^<]*)<\/em>/g, "*$1*")
      .replace(/<strong>([^<]*)<\/strong>/g, "**$1**")
      // "\," outside math ("1\,025 g" in a Diagnos question, 2026-08-15) is an
      // unambiguous LaTeX thin space — same family as the step arrows above.
      .replace(/\\,/g, " ")
      // "4{,}5" — the math-mode decimal-comma escape authored in prose — printed
      // its braces (Integraler/Tillämpningar 1b, 2026-08-15).
      .replace(/(\d)\{,\}(\d)/g, "$1,$2")
      // Authored "\n" escapes reach prose as literal backslash-n (35 places in
      // Ma3 Addition och subtraktion, 2026-08-15) — decode to a real newline
      // (pre-wrap renders it). Only before capital/whitespace so \nabla-style
      // commands survive.
      .replace(/\\n(?=[A-ZÅÄÖ\s•–-])/g, "\n")
      // "\\[6pt]" spaced breaks and "\%" escapes in prose print verbatim
      // (Tillämpningar 1c, 2026-08-15) — decode both.
      .replace(/\\\\\[\d+pt\]\s*/g, "\n")
      .replace(/\\%/g, "%")
  );
}

/**
 * Print-only: a long inline chain "A = B = C = D" (or with \Rightarrow /
 * \approx) is set as an aligned display block with one step per line, the
 * relation leading each new line. KaTeX cannot wrap an inline formula, and
 * one integral chain measured 659 px against a 619 px facit column — the
 * whole facit box grew to fit it and ran off the paper (Integral mellan två
 * kurvor, Ekvationer med identiteter; PDF-körningen 2026-09-14). Textbook
 * form for a multi-step calculation anyway. Only chains longer than
 * `maxTecken` with at least two top-level relations are touched; the on-screen
 * runner never calls this, so nothing changes on the site.
 */
export function brytLangaKedjor(source: string, maxTecken = 70): string {
  const segment = parseSegments(source);
  // Punctuation right after a chain moves inside the block: a display block
  // is its own line, so a following "." would otherwise sit alone on the
  // next one (Integral mellan två kurvor, 2026-09-14).
  const absorberad = new Set<number>();
  return segment
    .map((s, i) => {
      if (absorberad.has(i)) return source.slice(s.start, s.end).replace(/^[.,;:]/, "");
      if (s.kind !== "math" || s.block) return source.slice(s.start, s.end);
      // An implication chain is split at the arrows only — each line is then a
      // whole equation, left-aligned. An equality chain is split at every
      // "=" / "\approx" and aligned on the relation, textbook style.
      const harPil = /\\Rightarrow(?![a-zA-Z])/.test(s.value);
      const steg = delaVidToppnivaRelation(s.value, harPil ? ["\\Rightarrow"] : ["=", "\\approx"]);
      if (s.value.length <= maxTecken || steg.length < 3) return source.slice(s.start, s.end);
      const rader = harPil
        ? steg.map((d) => `&${d.relation} ${d.uttryck}`)
        : [`${steg[0].uttryck} &${steg[1].relation} ${steg[1].uttryck}`, ...steg.slice(2).map((d) => `&${d.relation} ${d.uttryck}`)];
      const nasta = segment[i + 1];
      const tecken = nasta && nasta.kind === "text" ? (nasta.value.match(/^[.,;:]/)?.[0] ?? "") : "";
      if (tecken) absorberad.add(i + 1);
      return `$$\\begin{aligned} ${rader.join(" \\\\ ")}${tecken} \\end{aligned}$$`;
    })
    .join("");
}

/**
 * Splits a formula at its top-level relations. Depth counts {}, (), [] and
 * \left…\right, so "\dfrac{a}{b} = c" splits at the "=" but "f(x=1)" does not.
 */
function delaVidToppnivaRelation(
  tex: string,
  relationer: string[],
): { relation: string; uttryck: string }[] {
  const delar: { relation: string; uttryck: string }[] = [];
  let relation = "";
  let start = 0;
  let djup = 0;
  const kommandon = relationer.filter((r) => r.startsWith("\\")).map((r) => r.slice(1));
  for (let i = 0; i < tex.length; i++) {
    const ch = tex[i];
    if (ch === "\\") {
      if (tex.startsWith("\\left", i)) djup++;
      else if (tex.startsWith("\\right", i)) djup--;
      const kommando = kommandon.length
        ? tex.slice(i).match(new RegExp(`^\\\\(${kommandon.join("|")})(?![a-zA-Z])`))
        : null;
      if (kommando && djup === 0) {
        delar.push({ relation, uttryck: tex.slice(start, i).trim() });
        relation = kommando[0];
        i += kommando[0].length - 1;
        start = i + 1;
        continue;
      }
      i++; // skip the escaped character / first letter of a command
      continue;
    }
    if (ch === "{" || ch === "(" || ch === "[") djup++;
    else if (ch === "}" || ch === ")" || ch === "]") djup--;
    else if (ch === "=" && relationer.includes("=") && djup === 0 && tex[i - 1] !== "&") {
      delar.push({ relation, uttryck: tex.slice(start, i).trim() });
      relation = "=";
      start = i + 1;
    }
  }
  delar.push({ relation, uttryck: tex.slice(start).trim() });
  return delar.filter((d) => d.uttryck.length > 0 || d.relation === "");
}
